import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe, NgxValidateCoreModule } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CxInputPendingLoaderDirective } from 'src/app/shared/directives/cx-input-pending-loader.directive';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { DeclineReasonDto } from 'src/core/models/decline-reason/decline-reason.dto';
import { QueueDto } from 'src/app/administration/settings/queues/models/queue.dto';
import { DeclineReasonService } from '../../services/decline-reason.service';

@Component({
  selector: 'app-decline-reason-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LocalizationPipe,
    NgxValidateCoreModule,
    DialogModule,
    InputTextModule,
    MultiSelectModule,
    CxInputPendingLoaderDirective,
    FormActionsComponent,
  ],
  templateUrl: './decline-reason-form-dialog.component.html',
})
export class DeclineReasonFormDialogComponent extends BaseFormComponent<DeclineReasonDto> implements OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly crudService = inject(CrudService);
  private readonly declineReasonService = inject(DeclineReasonService);
  private readonly destroy$ = new Subject<void>();

  private currentReasonName = '';

  availableQueues: QueueDto[] = [];
  selectedQueueIds: number[] = [];

  @Input() visible = false;
  @Input() reasonId: number | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  get isEditMode(): boolean {
    return this.reasonId != null;
  }

  get isValidating(): boolean {
    return this.form?.pending ?? false;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isValidating;
  }

  get headerKey(): string {
    return this.isEditMode
      ? 'Settings::WorkEnvironment:DeclineReasons:EditReasonTitle'
      : 'Settings::WorkEnvironment:DeclineReasons:NewReasonTitle';
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      name: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [this.uniqueNameValidator()],
        updateOn: 'change',
      }),
    });
  }

  protected override afterFormInit(): void {
    this.syncFormFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.availableQueues.length === 0) {
      this.loadQueues();
    }
    this.syncFormFromInputs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (this.isValidating) {
      return;
    }

    this.onSubmit();
  }

  protected submitRequest(): Observable<unknown> {
    const name = String(this.form.getRawValue().name ?? '').trim();

    const payload: Partial<DeclineReasonDto> = {
      name,
      queueIds: [...this.selectedQueueIds],
    };

    if (this.isEditMode) {
      return this.crudService.update<DeclineReasonDto>(DeclineReasonDto, {
        id: this.reasonId!,
        ...payload,
      });
    }

    return this.crudService.insertWithoutId(DeclineReasonDto, payload);
  }

  protected override onSubmitSuccess(): void {
    super.onSubmitSuccess();
    this.saved.emit();
    this.visibleChange.emit(false);
  }

  private loadQueues(): void {
    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [],
    };

    this.crudService.get<QueueDto>(QueueDto, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListResponseDto<QueueDto>) => {
          this.availableQueues = (response.items ?? []).filter(q => q.isActive);
          this.cdr.markForCheck();
        },
      });
  }

  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return of(null);
      }

      if (
        this.reasonId != null &&
        value.toLocaleLowerCase() === this.currentReasonName.toLocaleLowerCase()
      ) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() =>
          this.declineReasonService.checkNameUnique(value, this.reasonId ?? undefined),
        ),
        map(isUnique => (isUnique ? null : { nameNotUnique: true })),
        catchError(() => of(null)),
        take(1),
      );
    };
  }

  private syncFormFromInputs(): void {
    if (!this.visible || !this.form) {
      return;
    }

    if (this.reasonId == null) {
      this.currentReasonName = '';
      this.selectedQueueIds = [];
      this.resetForm({ name: '' });
      return;
    }

    this.crudService.getById<DeclineReasonDto>(DeclineReasonDto, this.reasonId).subscribe({
      next: reason => {
        const name = reason?.name ?? '';
        this.currentReasonName = name.trim();
        this.selectedQueueIds = [...(reason?.queueIds ?? [])];
        this.resetForm({ name });
      },
      error: () => {
        this.currentReasonName = '';
        this.selectedQueueIds = [];
        this.resetForm({ name: '' });
      },
    });
  }
}
